/*
Segmented button — single-select tab bar with 2-4 options. Mirrors the
Compose SegmentedButton row used by Android (Arboretum's Catalogue/Historique
+ Par fréquence/Par genre, Remarquables' Catalogue/Historique). Plain DOM,
no framework, owns its event listeners and surfaces a destroy() so the
caller can clean up on screen unmount.

Usage:
    const vCtrl = mountTabSelector({
        host: vSelectorHost,
        options: [
            { id: 'catalogue', label: 'Catalogue' },
            { id: 'historique', label: 'Historique' },
        ],
        activeId: 'catalogue',
        onChange: (inId) => { ... },
    })
    // later: vCtrl.destroy()
*/

export interface TabSelectorOption
{
	id: string
	label: string
}

export interface TabSelectorArgs
{
	host: HTMLElement
	options: TabSelectorOption[]
	activeId: string
	onChange: (inId: string) => void
}

export interface TabSelectorController
{
	destroy(): void
	setActive(inId: string): void  // mutates the visual state without firing onChange — useful when the parent rehydrates
}

export function mountTabSelector(inArgs: TabSelectorArgs): TabSelectorController
{
	const vHost = inArgs.host
	vHost.classList.add('tab-selector')
	vHost.setAttribute('role', 'tablist')
	vHost.innerHTML = inArgs.options.map((inOpt) => `
		<button
			class="tab-selector-option ${inOpt.id === inArgs.activeId ? 'tab-selector-option-active' : ''}"
			role="tab"
			aria-selected="${inOpt.id === inArgs.activeId ? 'true' : 'false'}"
			data-tab-id="${escapeAttr(inOpt.id)}"
		>${escapeHtml(inOpt.label)}</button>
	`).join('')

	const vCleanups: Array<() => void> = []  // remove() callbacks per option, run on destroy

	const vSetActive = (inId: string): void => {
		vHost.querySelectorAll<HTMLElement>('.tab-selector-option').forEach((inBtn) => {
			const vIsActive = inBtn.dataset.tabId === inId
			inBtn.classList.toggle('tab-selector-option-active', vIsActive)
			inBtn.setAttribute('aria-selected', vIsActive ? 'true' : 'false')
		})
	}

	vHost.querySelectorAll<HTMLElement>('.tab-selector-option').forEach((inBtn) => {
		const vOnClick = (): void => {
			const vId = inBtn.dataset.tabId
			if (!vId) return
			vSetActive(vId)
			inArgs.onChange(vId)
		}
		inBtn.addEventListener('click', vOnClick)
		vCleanups.push(() => inBtn.removeEventListener('click', vOnClick))
	})

	return {
		destroy(): void
		{
			for (const vCleanup of vCleanups) vCleanup()
			vHost.classList.remove('tab-selector')
			vHost.removeAttribute('role')
			vHost.innerHTML = ''
		},
		setActive: vSetActive,
	}
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
